import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { Icons } from '@/assets/icons/icons';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { ScrollArea } from '../ScrollArea';

// based on shadcn - https://ui.shadcn.com/docs/components/command

export const CommandRoot = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    onKeyDown={(e) => e.stopPropagation()}
    className={cn(
      'flex h-full w-full rounded-xl flex-col overflow-hidden bg-transparent text-popover-foreground',
      className
    )}
    {...props}
  />
));
CommandRoot.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends React.ComponentPropsWithoutRef<
  typeof Dialog.Root
> {}

export const CommandDialogRoot = ({
  children,
  ...props
}: CommandDialogProps) => {
  return (
    <Dialog.Root {...props}>
      <Dialog.Content className="overflow-hidden rounded-2 p-0 shadow-lg max-w-xl pb-1 top-[20%] translate-y-0">
        <CommandRoot className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-foreground-muted [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3">
          {children}
        </CommandRoot>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    showSearchIcon?: boolean;
    shortcut?: string;
  }
>(({ className, showSearchIcon = true, shortcut, ...props }, ref) => {
  return (
    <div
      className="flex items-center border-b border-border-strong px-3 mb-1"
      cmdk-input-wrapper=""
    >
      {showSearchIcon && (
        <Icons.search className="mr-2 h-4 w-4 shrink-0 text-foreground-muted" />
      )}
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-11 flex-1 text-foreground rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-foreground-muted disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
      {shortcut && (
        <span className="inline-flex select-none items-center justify-center rounded border border-border-surface bg-secondary px-2 py-0.5 text-1 text-foreground-muted">
          <kbd>{shortcut}</kbd>
        </span>
      )}
    </div>
  );
});

CommandInput.displayName = CommandPrimitive.Input.displayName;

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
    /** Classes for the scroll container wrapping the list */
    containerClassName?: string;
    /** Ref for the scrollable container wrapping the list */
    containerRef?: React.Ref<HTMLDivElement>;
  }
>(({ className, containerClassName, containerRef, ...props }, ref) => (
  <ScrollArea
    ref={containerRef}
    className={cn('max-h-[360px]', containerClassName)}
  >
    <CommandPrimitive.List
      ref={ref}
      className={cn('overflow-y-auto overflow-x-hidden', className)}
      {...props}
    />
  </ScrollArea>
));

CommandList.displayName = CommandPrimitive.List.displayName;

export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={cn('py-6 text-center text-sm text-foreground-subtle', className)}
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-foreground-muted',
      className
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 h-px', className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'bg-surface hover:bg-surface-hover relative flex select-none items-center rounded-md px-2 py-2 gap-4 text-xs outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      'data-[selected=true]:bg-surface-active data-[current=true]:bg-surface-active',
      className
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

export const CommandLoading = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Loading>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Loading
    className={cn(
      'bg-surface hover:bg-surface-hover relative flex select-none items-center rounded-md px-2 py-2 gap-4 text-xs outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      className
    )}
    ref={ref}
    {...props}
  />
));

export const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'tracking-widest ml-auto text-xs text-foreground-muted',
        className
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';
